import { useState } from 'react';
import { sameAccount } from '../../../utils/accountIds';
import { Account } from '../../../types';
import { getAccounts, getAccountScope, getUser } from '../../../store/User';
import { userInSkipRoles } from '../../../store/Role';
import { LocationScope } from './serviceRequestModel';

/**
 * The account a request is filed under: the location's, else the one account selected in the header, else
 * the customer's own pick; and whether the form offers that pick at all.
 */
export function useAccountSelection(location?: LocationScope) {
  const deriveDefaultAccount = (): Account | null => {
    const scope = getAccountScope();
    const ofLocation =
      location &&
      [...(scope ?? []), ...(getAccounts() ?? [])].find((account) => sameAccount(account.id, location.accountId));
    return ofLocation ?? (scope?.length === 1 ? scope[0] : null);
  };
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(deriveDefaultAccount);
  const resetToDefault = (): Account | null => {
    const account = deriveDefaultAccount();
    setSelectedAccount(account);
    return account;
  };

  // Only multi-account customers pick an account; internal roles (Admin, Advisor, CSM,
  // SDM, Executive, Support) scope via the global header selector instead.
  const showAccountSelector =
    !userInSkipRoles((getUser()?.roles ?? []).map((role) => role.name)) && (getAccounts()?.length ?? 0) > 1;
  const accountSelectionMissing = showAccountSelector && !selectedAccount;
  // Offer the globally selected accounts; with none selected in the header, fall back to
  // all accessible accounts so the empty state can be resolved from within the form.
  const accountScope = getAccountScope();
  const selectableAccounts = accountScope?.length ? accountScope : (getAccounts() ?? []);
  const accountOptions = [...selectableAccounts].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  return {
    resetToDefault,
    selectedAccount,
    setSelectedAccount,
    showAccountSelector,
    accountSelectionMissing,
    accountOptions
  };
}
